import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast, Toaster } from "sonner";
import DashboardShell from "../../components/layout/DashboardShell";
import { Pill } from "../../components/common/Primitives";
import api, { formatApiError } from "../../lib/api";
import { formatDateTime } from "../../lib/datetime";
import { Landmark, RefreshCw, Search, Filter, Inbox, Phone, Mail, MapPin, ChevronRight } from "lucide-react";

const STATUS_OPTIONS = ["new", "contacted", "qualified", "converted", "junk", "dropped"];
const STATUS_TONE = {
  new: "bg-blue-100 text-blue-800 border-blue-200",
  contacted: "bg-amber-100 text-amber-800 border-amber-200",
  qualified: "bg-emerald-100 text-emerald-800 border-emerald-200",
  converted: "bg-emerald-200 text-emerald-900 border-emerald-300",
  junk: "bg-gray-100 text-gray-600 border-gray-200",
  dropped: "bg-red-100 text-red-800 border-red-200",
};

const fmtINR = (n) => {
  if (!n && n !== 0) return "—";
  if (n >= 1e7) return `₹${(n / 1e7).toFixed(n % 1e7 === 0 ? 0 : 2)} Cr`;
  if (n >= 1e5) return `₹${(n / 1e5).toFixed(0)} L`;
  return `₹${(n / 1000).toFixed(0)} K`;
};

export default function AdminDebtLeads() {
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({ all: 0, by_section: {}, by_product: {}, by_status: {} });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [product, setProduct] = useState("");
  const [section, setSection] = useState("");
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (status) params.status = status;
      if (product) params.product = product;
      if (section) params.source_section = section;
      if (q) params.q = q;
      const r = await api.get("/admin/debt/leads", { params });
      setRows(r.data?.items || []);
      setTotals(r.data?.totals || { all: 0, by_section: {}, by_product: {}, by_status: {} });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [status, product, section, q]);

  useEffect(() => { load(); }, [load]);

  const productList = useMemo(() => Object.keys(totals.by_product || {}).sort(), [totals]);
  const sectionList = useMemo(() => Object.keys(totals.by_section || {}).sort(), [totals]);

  const updateStatus = async (leadId, newStatus) => {
    try {
      await api.patch(`/admin/debt/leads/${leadId}`, { status: newStatus });
      toast.success(`Status updated → ${newStatus}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <DashboardShell>
      <div className="space-y-6" data-testid="admin-debt-leads">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">VENTURAZ MONEY · LEADS INBOX</div>
            <h1 className="font-display text-3xl text-ink flex items-center gap-2 mt-1">
              <Inbox size={22} className="text-[#7A4E1D]" /> Debt Leads
            </h1>
            <p className="text-sm text-ink-soft mt-1 max-w-2xl">
              Every lead captured from the debt marketing surfaces — hero cards, product wizards, financial calculators,
              become-partner, mini-form. Each lead is tagged with the exact page &amp; section it came from.
            </p>
          </div>
          <button onClick={load} className="inline-flex items-center gap-1 text-sm px-3 py-2 border border-ink/15 rounded hover:bg-ink/[0.03]" data-testid="admin-debt-leads-refresh">
            <RefreshCw size={13} /> Refresh
          </button>
        </div>

        {/* KPI strip */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <StatCard label="Total leads" value={totals.all} testId="lead-stat-all" />
          {STATUS_OPTIONS.slice(0, 5).map((s) => (
            <StatCard key={s} label={s} value={totals.by_status?.[s] || 0} tone={STATUS_TONE[s]} testId={`lead-stat-${s}`} />
          ))}
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2 flex-wrap border border-ink/10 bg-white rounded-lg p-3">
          <div className="relative flex-1 min-w-56">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, email, phone, ID…"
              className="w-full pl-8 pr-3 py-2 text-sm border border-ink/15 rounded focus:outline-none focus:border-[#7A4E1D]"
              data-testid="admin-debt-leads-search"
            />
          </div>
          <FilterSelect value={status} onChange={setStatus} options={[{ v: "", l: "All statuses" }, ...STATUS_OPTIONS.map((s) => ({ v: s, l: s }))]} testId="admin-debt-leads-filter-status" />
          <FilterSelect value={product} onChange={setProduct} options={[{ v: "", l: "All products" }, ...productList.map((p) => ({ v: p, l: p }))]} testId="admin-debt-leads-filter-product" />
          <FilterSelect value={section} onChange={setSection} options={[{ v: "", l: "All sources" }, ...sectionList.map((s) => ({ v: s, l: s }))]} testId="admin-debt-leads-filter-section" />
        </div>

        {/* Table */}
        <div className="border border-ink/10 rounded-lg bg-white overflow-hidden">
          <div className="grid grid-cols-12 gap-2 px-4 py-2.5 text-[10px] font-mono uppercase tracking-widest text-ink-muted border-b border-ink/[0.06] bg-[#FBFAF5]">
            <div className="col-span-3">Lead · Contact</div>
            <div className="col-span-2">Product · Amount</div>
            <div className="col-span-3">Source</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-2 text-right">Created</div>
          </div>
          {loading && <div className="p-8 text-center text-sm text-ink-muted">Loading leads…</div>}
          {!loading && rows.length === 0 && (
            <div className="p-10 text-center text-sm text-ink-muted" data-testid="admin-debt-leads-empty">
              No leads yet. Once a visitor submits from the debt marketing surfaces, they&apos;ll appear here.
            </div>
          )}
          {!loading && rows.map((row) => (
            <button
              key={row.lead_id}
              onClick={() => setSelected(row)}
              className="w-full grid grid-cols-12 gap-2 px-4 py-3 border-b border-ink/[0.05] last:border-0 hover:bg-[#FBFAF5] text-left transition-colors"
              data-testid={`admin-debt-lead-row-${row.lead_id}`}
            >
              <div className="col-span-3">
                <div className="font-heading text-sm text-ink truncate">{row.contact_name || "—"}</div>
                <div className="text-[11px] text-ink-muted flex items-center gap-2 mt-0.5">
                  {row.contact_phone && <span className="inline-flex items-center gap-0.5"><Phone size={10} /> {row.contact_phone}</span>}
                  {row.contact_email && <span className="inline-flex items-center gap-0.5 truncate"><Mail size={10} /> {row.contact_email}</span>}
                </div>
                <div className="text-[10px] font-mono text-ink-muted mt-0.5">{row.lead_id}</div>
              </div>
              <div className="col-span-2">
                <div className="text-sm text-ink font-heading">{row.product || "—"}</div>
                <div className="text-[11px] text-ink-muted">{fmtINR(row.amount_inr)} · {row.tenure_months || "—"}m</div>
              </div>
              <div className="col-span-3">
                <div className="text-sm text-ink truncate" title={row.source_page}>{row.source_page}</div>
                <div className="mt-0.5"><Pill tone="gold">{row.source_section}</Pill></div>
                {row.partial && <div className="mt-1 text-[10px] font-mono text-amber-700 uppercase tracking-widest">Partial · step {row.wizard_step}</div>}
              </div>
              <div className="col-span-2">
                <span
                  className={`inline-block text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full border ${STATUS_TONE[row.status] || STATUS_TONE.new}`}
                  data-testid={`admin-debt-lead-status-${row.lead_id}`}
                >
                  {row.status}
                </span>
              </div>
              <div className="col-span-2 text-right">
                <div className="text-[11px] text-ink-muted">{formatDateTime(row.created_at)}</div>
                <ChevronRight size={14} className="inline text-ink-muted mt-1" />
              </div>
            </button>
          ))}
        </div>
      </div>

      {selected && (
        <LeadDrawer lead={selected} onClose={() => setSelected(null)} onUpdated={load} updateStatus={updateStatus} />
      )}
      <Toaster richColors position="top-right" />
    </DashboardShell>
  );
}

function StatCard({ label, value, tone, testId }) {
  return (
    <div className={`border rounded-lg px-3 py-2 bg-white ${tone ? "" : "border-ink/10"}`} data-testid={testId}>
      <div className={`text-[9px] font-mono uppercase tracking-widest ${tone ? "" : "text-ink-muted"}`}>{label}</div>
      <div className="font-display text-2xl text-ink mt-0.5">{value || 0}</div>
    </div>
  );
}

function FilterSelect({ value, onChange, options, testId }) {
  return (
    <div className="relative">
      <Filter size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-muted pointer-events-none" />
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}
        className="pl-7 pr-3 py-2 text-sm border border-ink/15 rounded bg-white focus:outline-none focus:border-[#7A4E1D]">
        {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    </div>
  );
}

function LeadDrawer({ lead, onClose, onUpdated, updateStatus }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const submitNote = async () => {
    if (!note.trim()) return;
    setBusy(true);
    try {
      await api.patch(`/admin/debt/leads/${lead.lead_id}`, { note });
      toast.success("Note added");
      setNote("");
      onUpdated();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40" onClick={onClose} data-testid="admin-debt-lead-drawer">
      <div
        className="absolute right-0 top-0 h-full w-full max-w-lg bg-white border-l border-ink/10 shadow-2xl overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 border-b border-ink/10 flex items-start justify-between">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#7A4E1D]">{lead.lead_id}</div>
            <div className="font-display text-xl text-ink mt-0.5">{lead.contact_name || "Unnamed"}</div>
            <div className="text-[11px] text-ink-muted mt-1 flex gap-3 flex-wrap">
              {lead.contact_phone && <span className="inline-flex items-center gap-1"><Phone size={11} /> {lead.contact_phone}</span>}
              {lead.contact_email && <span className="inline-flex items-center gap-1"><Mail size={11} /> {lead.contact_email}</span>}
              {lead.city && <span className="inline-flex items-center gap-1"><MapPin size={11} /> {lead.city}</span>}
            </div>
          </div>
          <button onClick={onClose} className="text-ink-muted hover:text-ink text-sm" data-testid="admin-debt-lead-drawer-close">✕</button>
        </div>

        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3 text-[13px]">
            <Info k="Product" v={lead.product || "—"} />
            <Info k="Amount" v={fmtINR(lead.amount_inr)} />
            <Info k="Tenure" v={lead.tenure_months ? `${lead.tenure_months} months` : "—"} />
            <Info k="Monthly income" v={lead.monthly_income ? `₹${Number(lead.monthly_income).toLocaleString("en-IN")}` : "—"} />
            <Info k="Employment" v={lead.employment || "—"} />
            <Info k="Status" v={lead.status} />
          </div>

          <div className="border border-ink/10 rounded-lg p-3 bg-[#FBFAF5]">
            <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted mb-1">Source</div>
            <div className="text-[13px] text-ink"><b>Page:</b> {lead.source_page}</div>
            <div className="text-[13px] text-ink mt-1"><b>Section:</b> {lead.source_section}</div>
            {lead.partial && <div className="mt-1 text-[11px] text-amber-800">Wizard drop-off at step {lead.wizard_step}</div>}
          </div>

          {lead.extra && Object.keys(lead.extra).length > 0 && (
            <div className="border border-ink/10 rounded-lg p-3">
              <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted mb-1">Extra fields</div>
              <pre className="text-[11px] text-ink whitespace-pre-wrap font-mono">{JSON.stringify(lead.extra, null, 2)}</pre>
            </div>
          )}

          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted mb-1">Change status</div>
            <div className="flex flex-wrap gap-1.5">
              {STATUS_OPTIONS.map((s) => (
                <button
                  key={s}
                  disabled={s === lead.status || busy}
                  onClick={() => updateStatus(lead.lead_id, s)}
                  className={`text-[11px] font-mono uppercase tracking-widest px-2 py-1 rounded-full border ${STATUS_TONE[s]} disabled:opacity-40`}
                  data-testid={`admin-debt-lead-status-btn-${s}`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted mb-1">Add note</div>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              className="w-full text-sm border border-ink/15 rounded p-2 focus:outline-none focus:border-[#7A4E1D]"
              placeholder="Called at 3pm, offered term-loan @ 10.25%…"
              data-testid="admin-debt-lead-note-input"
            />
            <button
              onClick={submitNote}
              disabled={busy || !note.trim()}
              className="mt-2 px-4 py-2 rounded-md bg-[#7A4E1D] text-[#FFF3D9] text-sm font-heading hover:bg-[#5c3a15] disabled:opacity-50"
              data-testid="admin-debt-lead-note-save"
            >
              {busy ? "Saving…" : "Save note"}
            </button>
          </div>

          {lead.notes && lead.notes.length > 0 && (
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted mb-1">Notes history</div>
              <div className="space-y-2">
                {lead.notes.map((n, i) => (
                  <div key={i} className="border border-ink/[0.06] rounded p-2 bg-white">
                    <div className="text-[10px] font-mono text-ink-muted">{n.by} · {formatDateTime(n.at)}</div>
                    <div className="text-sm text-ink mt-0.5">{n.text}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Info({ k, v }) {
  return (
    <div>
      <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">{k}</div>
      <div className="text-[13px] text-ink font-heading mt-0.5">{v}</div>
    </div>
  );
}
